import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { safeHtmlProps } from "../utils/sanitizeHtml";
import { Check, Printer, Shield, ShieldCheck, X } from "@phosphor-icons/react";

const fmtInr = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const numToIndianWords = (num) => {
  if (!num) return "Zero";
  const rounded = Math.round(Number(num) * 100) / 100;
  const [intPart, decPart] = rounded.toFixed(2).split(".");
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const twoDigit = (n) => n < 20 ? a[n] : `${b[Math.floor(n / 10)]}${n % 10 ? " " + a[n % 10] : ""}`;
  const threeDigit = (n) => n >= 100 ? `${a[Math.floor(n / 100)]} Hundred${n % 100 ? " " + twoDigit(n % 100) : ""}` : twoDigit(n);
  const n = parseInt(intPart, 10);
  const crore = Math.floor(n / 10000000), rest1 = n % 10000000;
  const lakh = Math.floor(rest1 / 100000), rest2 = rest1 % 100000;
  const thousand = Math.floor(rest2 / 1000), rest3 = rest2 % 1000;
  let out = "";
  if (crore) out += threeDigit(crore) + " Crore ";
  if (lakh) out += threeDigit(lakh) + " Lakh ";
  if (thousand) out += threeDigit(thousand) + " Thousand ";
  if (rest3) out += threeDigit(rest3);
  const paise = parseInt(decPart, 10);
  return `${(out.trim() || "Zero")} Rupees${paise ? ` and ${twoDigit(paise)} Paise` : ""} only`;
};

const PublicQuote = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [acceptModal, setAcceptModal] = useState(false);
  const [acceptForm, setAcceptForm] = useState({ acceptor_name: "", acceptor_email: "", acceptor_note: "" });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/quote/${token}`)
      .then(r => {
        setData(r.data);
        setAcceptForm(f => ({ ...f, acceptor_name: r.data.doc.customer_name || "", acceptor_email: r.data.doc.customer_email || "" }));
      })
      .catch(e => setError(e?.response?.data?.detail || "Unable to load quote"))
      .finally(() => setLoading(false));
  }, [token]);

  const accept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/public/quote/${token}/accept`, acceptForm);
      toast.success("Quotation accepted — our team will be in touch shortly.");
      // Refresh
      const r = await axios.get(`${API}/public/quote/${token}`);
      setData(r.data);
      setAcceptModal(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Acceptance failed"); }
    finally { setAccepting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-slate-50"><div className="loading-spinner" /></div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="bg-white border border-slate-200 p-10 text-center max-w-md">
        <Shield size={40} className="mx-auto text-slate-300 mb-3" />
        <h1 className="text-xl font-bold mb-1" style={{fontFamily: 'Outfit, sans-serif'}}>Link unavailable</h1>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const { doc, settings: s } = data;
  const accent = s.template_accent_color || "#002FA7";
  const typeLabel = { estimation: "Quotation", proforma: "Proforma Invoice", invoice: "Tax Invoice" }[doc.doc_type] || "Document";
  const accepted = !!doc.customer_acceptance?.accepted;
  const sellerStateCode = (s.state_code || s.gstin?.slice(0, 2) || "").toString();
  const buyerStateCode = (doc.place_of_supply || "").split("-")[0] || (doc.customer_gstin || "").slice(0, 2);
  const isInterState = !!(buyerStateCode && sellerStateCode && buyerStateCode !== sellerStateCode);
  const gstRate = Number(doc.gst_rate || s.default_gst_rate || 0);
  const cgst = isInterState ? 0 : (doc.tax_amount || 0) / 2;
  const sgst = isInterState ? 0 : (doc.tax_amount || 0) / 2;
  const igst = isInterState ? (doc.tax_amount || 0) : 0;

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:py-0 print:bg-white" data-testid="public-quote-page">
      {/* Sticky bar (hidden on print) */}
      <div className="max-w-4xl mx-auto mb-4 px-4 flex items-center justify-between no-print">
        <div className="flex items-center gap-2">
          <Shield size={18} style={{ color: accent }} weight="fill" />
          <span className="text-sm font-medium text-slate-700">Secure share from {s.company_name || "SwifTech India"}</span>
        </div>
        <div className="flex gap-2">
          <button onClick={() => window.print()} data-testid="public-print-btn" className="bg-white border border-slate-300 hover:bg-slate-50 px-3 py-1.5 text-sm flex items-center gap-1.5">
            <Printer size={14} /> Print / PDF
          </button>
          {!accepted && doc.doc_type === "estimation" && (
            <button onClick={() => setAcceptModal(true)} data-testid="public-accept-btn"
              className="text-white px-4 py-1.5 text-sm font-bold flex items-center gap-1.5 shadow-sm hover:opacity-90"
              style={{ backgroundColor: accent }}>
              <Check size={14} weight="bold" /> Accept Quotation
            </button>
          )}
        </div>
      </div>

      {accepted && (
        <div className="max-w-4xl mx-auto mb-4 px-4 no-print" data-testid="public-accepted-banner">
          <div className="bg-green-50 border-l-4 border-green-500 px-5 py-3 flex items-start gap-3">
            <ShieldCheck size={22} className="text-green-600 mt-0.5" weight="fill" />
            <div>
              <div className="font-bold text-green-900 text-sm">Quotation accepted</div>
              <div className="text-xs text-green-700">by {doc.customer_acceptance.acceptor_name} on {(doc.customer_acceptance.accepted_at || "").slice(0, 10)}. Our team has been notified.</div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice body */}
      <div className="max-w-4xl mx-auto bg-white shadow-md print:shadow-none print:max-w-none" id="public-doc">
        <div className="p-8 print:p-10 relative" style={{ fontFamily: "Inter, Outfit, sans-serif" }}>
          {doc.letterhead_data_url && (
            <img src={doc.letterhead_data_url} alt="" aria-hidden="true"
              className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
              style={{ opacity: doc.letterhead_opacity || 0.08, zIndex: 0 }} />
          )}
          <div className="relative" style={{ zIndex: 1 }}>
            {/* Header */}
            <div className="flex justify-between items-start mb-6 pb-6" style={{ borderBottom: `2px solid ${accent}` }}>
              <div className="flex gap-4 items-start">
                {s.logo_url && <img src={s.logo_url} alt="Logo" className="w-20 h-20 object-contain" />}
                <div>
                  <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{s.company_name || "SwifTech India"}</h1>
                  {s.tagline && <p className="text-xs text-slate-600 mt-0.5">{s.tagline}</p>}
                  <div className="text-xs text-slate-600 mt-2">
                    {[s.address_line1, s.address_line2].filter(Boolean).join(", ")}
                    {(s.city || s.state) && <div>{[s.city, s.state, s.pincode].filter(Boolean).join(", ")}</div>}
                    {s.phone && <div>📞 {s.phone}</div>}
                    {s.email && <div>✉ {s.email}</div>}
                    {s.gstin && <div className="font-mono mt-1"><b>GSTIN:</b> {s.gstin}</div>}
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="inline-block bg-slate-100 px-4 py-2 border border-slate-300">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{typeLabel}</div>
                  <div className="text-2xl font-bold mt-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: accent }}>#{doc.number}</div>
                </div>
                <table className="text-xs mt-3 ml-auto">
                  <tbody>
                    <tr><td className="pr-3 text-slate-500">Date</td><td className="font-semibold">{(doc.created_at || "").slice(0, 10)}</td></tr>
                    {doc.reference_no && <tr><td className="pr-3 text-slate-500">Reference</td><td className="font-semibold font-mono">{doc.reference_no}</td></tr>}
                    {doc.expiry_date && <tr><td className="pr-3 text-slate-500">Valid Till</td><td className="font-semibold">{doc.expiry_date}</td></tr>}
                    {doc.salesperson && <tr><td className="pr-3 text-slate-500">Salesperson</td><td className="font-semibold">{doc.salesperson}</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Bill To */}
            <div className="mb-5">
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 font-bold">Bill To</div>
              <div className="font-bold text-base">{doc.customer_name}</div>
              {doc.customer_address && <div className="text-xs text-slate-700">{doc.customer_address}</div>}
              {doc.customer_gstin && <div className="text-xs font-mono mt-1"><b>GSTIN:</b> {doc.customer_gstin}</div>}
            </div>

            {doc.subject && (
              <div className="mb-5 border-l-4 pl-3 text-sm" style={{ borderColor: accent }}>
                <div className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Subject</div>
                <div>{doc.subject}</div>
              </div>
            )}

            {/* Items */}
            <table className="w-full mb-6 text-sm">
              <thead>
                <tr className="text-white text-xs uppercase tracking-wider" style={{ backgroundColor: accent }}>
                  <th className="text-left p-2">#</th>
                  <th className="text-left p-2">Item / Description</th>
                  <th className="text-center p-2">HSN</th>
                  <th className="text-right p-2">Qty</th>
                  <th className="text-right p-2">Rate</th>
                  <th className="text-right p-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(doc.line_items || []).map((li, i) => (
                  <tr key={li.row_id || `${doc.doc_id}-row-${i}`} className="border-b border-slate-200">
                    <td className="p-2">{i + 1}</td>
                    <td className="p-2 whitespace-pre-wrap break-words align-top">{li.description}</td>
                    <td className="p-2 text-center text-xs">{li.hsn_code || "-"}</td>
                    <td className="p-2 text-right">{li.quantity}</td>
                    <td className="p-2 text-right">{fmtInr(li.unit_price)}</td>
                    <td className="p-2 text-right font-semibold">{fmtInr(li.line_total || (li.quantity * li.unit_price) * (1 - (Number(li.discount) || 0) / 100))}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Totals */}
            <div className="flex justify-end mb-6">
              <div className="w-80 text-sm">
                <div className="flex justify-between py-1"><span className="text-slate-500">Sub Total</span><span>{fmtInr(doc.subtotal)}</span></div>
                {isInterState ? (
                  <div className="flex justify-between py-1"><span className="text-slate-500">IGST ({gstRate}%)</span><span>{fmtInr(igst)}</span></div>
                ) : (
                  <>
                    <div className="flex justify-between py-1"><span className="text-slate-500">CGST ({gstRate / 2}%)</span><span>{fmtInr(cgst)}</span></div>
                    <div className="flex justify-between py-1"><span className="text-slate-500">SGST ({gstRate / 2}%)</span><span>{fmtInr(sgst)}</span></div>
                  </>
                )}
                <div className="flex justify-between py-2 mt-1 bg-slate-100 px-3 border-y-2 border-slate-800 font-bold"><span>Total</span><span>{fmtInr(doc.total)}</span></div>
              </div>
            </div>

            <div className="text-xs border-t border-b border-slate-200 py-3 mb-5">
              <span className="text-slate-500 uppercase tracking-wider font-bold mr-2">Amount in words:</span>
              <span className="italic">{numToIndianWords(doc.total)}</span>
            </div>

            {/* Notes & Terms */}
            {(doc.notes_html || doc.terms_html) && (
              <div className="grid grid-cols-2 gap-6 text-xs mb-6">
                {doc.notes_html && <div><div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Notes</div><div className="doc-rich" {...safeHtmlProps(doc.notes_html)} /></div>}
                {doc.terms_html && <div><div className="uppercase tracking-wider text-slate-500 font-bold mb-1">Terms &amp; Conditions</div><div className="doc-rich" {...safeHtmlProps(doc.terms_html)} /></div>}
              </div>
            )}

            {/* Footer */}
            {doc.footer_html && <div className="mt-6 pt-4 border-t border-slate-200 text-xs text-slate-600 doc-rich" {...safeHtmlProps(doc.footer_html)} />}

            <div className="mt-6 text-center text-[11px] text-slate-400">This is a computer-generated document.</div>
          </div>
        </div>
      </div>

      {/* Bottom CTA (mobile-friendly) */}
      {!accepted && doc.doc_type === "estimation" && (
        <div className="max-w-4xl mx-auto mt-6 px-4 no-print">
          <button onClick={() => setAcceptModal(true)}
            className="w-full text-white py-4 text-base font-bold shadow-lg hover:opacity-90 flex items-center justify-center gap-2"
            style={{ backgroundColor: accent }}>
            <Check size={18} weight="bold" /> Accept this Quotation
          </button>
        </div>
      )}

      {/* Accept Modal */}
      {acceptModal && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setAcceptModal(false)}>
          <div className="bg-white max-w-md w-full shadow-2xl" onClick={e => e.stopPropagation()} data-testid="accept-modal">
            <div className="border-b border-slate-200 px-6 py-4 flex justify-between items-center">
              <h2 className="text-xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>Confirm Acceptance</h2>
              <button onClick={() => setAcceptModal(false)} className="p-2 hover:bg-slate-100 rounded-full"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-slate-600">By accepting, you confirm the scope, prices and T&amp;C in this {typeLabel.toLowerCase()}. Our team will reach out to finalize.</p>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Your Name</label>
                <input data-testid="accept-name" value={acceptForm.acceptor_name} onChange={e => setAcceptForm(f => ({ ...f, acceptor_name: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Your Email</label>
                <input data-testid="accept-email" type="email" value={acceptForm.acceptor_email} onChange={e => setAcceptForm(f => ({ ...f, acceptor_email: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider font-bold text-slate-600 block mb-1">Message (optional)</label>
                <textarea data-testid="accept-note" rows={3} value={acceptForm.acceptor_note} onChange={e => setAcceptForm(f => ({ ...f, acceptor_note: e.target.value }))}
                  className="w-full rounded border border-slate-300 focus:border-[#002FA7] p-2.5 outline-none text-sm"
                  placeholder="Any notes for the team (e.g., delivery date preferences)" />
              </div>
            </div>
            <div className="border-t border-slate-200 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setAcceptModal(false)} className="rounded border border-slate-300 px-4 py-2 hover:bg-slate-50 text-sm">Cancel</button>
              <button onClick={accept} disabled={accepting || !acceptForm.acceptor_name} data-testid="accept-confirm"
                className="rounded text-white px-5 py-2 font-bold text-sm disabled:opacity-60" style={{ backgroundColor: accent }}>
                {accepting ? "Accepting…" : "Confirm & Accept"}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @media print { .no-print { display: none !important; } body { background: white !important; } }
        .doc-rich ul { list-style: disc; padding-left: 1.25rem; margin: 0.25rem 0; }
        .doc-rich ol { list-style: decimal; padding-left: 1.5rem; margin: 0.25rem 0; }
        .doc-rich a { color: #002FA7; text-decoration: underline; }
        .doc-rich p { margin: 0.25rem 0; }
        .doc-rich b, .doc-rich strong { font-weight: 700; }
      `}</style>
    </div>
  );
};

export default PublicQuote;
